import { useCallback, useEffect, useRef, useState } from 'react';
import { AppWindow, Download, Maximize2, PictureInPicture2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { videoPipSupported } from '../../lib/share/pip';

/**
 * EL REPRODUCTOR (spec §4.12.6): `<video controls playsinline preload="metadata">` con la URL firmada
 * DIRECTA como `src` (6 h; en Safari cada trozo iría por el 302 si no), velocidad 1×/1,5×/2×, PiP
 * nativo, pantalla completa, Download y "Open in a window".
 *
 *  - `error` (URL vencida) → `onExpired()` relee `GET /:id`, se reasigna `src` y se conserva
 *    `currentTime`. COMO MUCHO `MAX_REFRESH` veces seguidas: si el objeto no existe (una `failed`
 *    sin archivo) cada URL nueva vuelve a dar `error`, y sin tope la pestaña pedía un `GET /:id`
 *    por segundo mientras estuviera abierta. Pasado el tope se muestra `rec.error`.
 *  - Primer `play` → `POST /:id/viewed`; al 90 % → `{ toEnd: true }` una sola vez. Es LA ÚNICA
 *    escritura del lado lector (LEY escribir-al-leer).
 */
const SPEEDS = [1, 1.5, 2] as const;
const MAX_REFRESH = 2;

export function RecordingPlayer({ id, mediaUrl, posterUrl, title, onExpired, autoPlay, fill, hideOpenWindow, hideDownload, local }: {
  id: number | null;
  mediaUrl: string | null;
  posterUrl?: string | null;
  title?: string | null;
  /** Vuelve a leer GET /:id y devuelve la URL nueva (null si ya no hay). */
  onExpired?: () => Promise<string | null>;
  autoPlay?: boolean;
  /** Borde a borde (ruta /rec/:id): sin bordes redondeados ni tope de alto. */
  fill?: boolean;
  hideOpenWindow?: boolean;
  /** El contenedor ya tiene su propio Download (la vista previa): no se duplica en la barra. */
  hideDownload?: boolean;
  /** Blob local de la vista previa: sin `viewed`, sin recarga por vencimiento. */
  local?: boolean;
}) {
  const { t } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [src, setSrc] = useState<string | null>(mediaUrl);
  const [speed, setSpeed] = useState<number>(1);
  const viewedRef = useRef(false);
  const toEndRef = useRef(false);
  const refreshingRef = useRef(false);
  const refreshesRef = useRef(0);
  const [broken, setBroken] = useState(false);

  useEffect(() => { setSrc(mediaUrl); refreshesRef.current = 0; setBroken(false); }, [mediaUrl]);
  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = speed; }, [speed, src]);

  const onError = useCallback(async () => {
    const v = videoRef.current;
    if (!v || local || !onExpired || refreshingRef.current) return;
    if (refreshesRef.current >= MAX_REFRESH) { setBroken(true); return; }
    refreshesRef.current += 1;
    refreshingRef.current = true;
    try {
      const at = v.currentTime;
      const wasPlaying = !v.paused;
      const url = await onExpired();
      if (!url) { setBroken(true); return; }
      setSrc(url);
      const restore = () => {
        try { v.currentTime = at; } catch { /* */ }
        if (wasPlaying) void v.play().catch(() => {});
        v.removeEventListener('loadedmetadata', restore);
      };
      v.addEventListener('loadedmetadata', restore);
    } finally { refreshingRef.current = false; }
  }, [local, onExpired]);

  const onPlay = useCallback(() => {
    if (local || id == null || viewedRef.current) return;
    viewedRef.current = true;
    void api.post(`/api/recordings/${id}/viewed`, {}).catch(() => {});
  }, [id, local]);

  const onTime = useCallback(() => {
    const v = videoRef.current;
    if (!v || local || id == null || toEndRef.current) return;
    if (v.duration && Number.isFinite(v.duration) && v.currentTime / v.duration >= 0.9) {
      toEndRef.current = true;
      void api.post(`/api/recordings/${id}/viewed`, { toEnd: true }).catch(() => {});
    }
  }, [id, local]);

  const pip = async () => {
    const v = videoRef.current as (HTMLVideoElement & { requestPictureInPicture?: () => Promise<unknown>; webkitSetPresentationMode?: (m: string) => void }) | null;
    if (!v) return;
    try {
      if (document.pictureInPictureElement) { await document.exitPictureInPicture(); return; }
      if (typeof v.requestPictureInPicture === 'function') await v.requestPictureInPicture();
      else if (typeof v.webkitSetPresentationMode === 'function') v.webkitSetPresentationMode('picture-in-picture');
    } catch { /* el navegador no quiso: sin PiP */ }
  };
  const fullscreen = () => {
    const el = wrapRef.current;
    const v = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    try {
      if (document.fullscreenElement) { void document.exitFullscreen(); return; }
      if (el?.requestFullscreen) void el.requestFullscreen();
      else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen();
    } catch { /* */ }
  };
  const openWindow = () => {
    if (id == null) return;
    window.open(`/rec/${id}`, '_blank', 'noopener,popup,width=1000,height=640');
  };
  const downloadHref = local ? src : id != null ? `/api/recordings/${id}/media` : null;

  return (
    <div ref={wrapRef} className={`rec-player${fill ? ' fill' : ''}`}>
      {broken ? (
        <div className="rec-video rec-video-empty">{t('rec.error')}</div>
      ) : src ? (
        <video ref={videoRef} className="rec-video" controls playsInline preload="metadata" poster={posterUrl ?? undefined} src={src}
          autoPlay={autoPlay} onError={() => { void onError(); }} onPlay={onPlay} onTimeUpdate={onTime} title={title ?? undefined}
          onLoadedMetadata={() => { refreshesRef.current = 0; }} />
      ) : (
        <div className="rec-video rec-video-empty">{t('rec.processing')}</div>
      )}
      <div className="rec-player-bar">
        <span className="rec-speed" role="group" aria-label={t('rec.speed')}>
          {SPEEDS.map((s) => (
            <button key={s} type="button" className={`btn ghost sm${speed === s ? ' on' : ''}`} onClick={() => setSpeed(s)} aria-pressed={speed === s}>{s}×</button>
          ))}
        </span>
        <span style={{ flex: 1 }} />
        {videoPipSupported() && <button type="button" className="btn ghost sm" onClick={() => { void pip(); }} title={t('rec.pip')}><PictureInPicture2 size={14} strokeWidth={1.75} /><span className="rec-btn-label"> {t('rec.pip')}</span></button>}
        <button type="button" className="btn ghost sm" onClick={fullscreen} title={t('rec.fullscreen')}><Maximize2 size={14} strokeWidth={1.75} /><span className="rec-btn-label"> {t('rec.fullscreen')}</span></button>
        {downloadHref && !hideDownload && (
          <a className="btn ghost sm" href={downloadHref} download={local ? (title || 'recording') : undefined} target={local ? undefined : '_blank'} rel="noreferrer" title={t('rec.download')}>
            <Download size={14} strokeWidth={1.75} /><span className="rec-btn-label"> {t('rec.download')}</span>
          </a>
        )}
        {!hideOpenWindow && !local && id != null && <button type="button" className="btn ghost sm" onClick={openWindow} title={t('rec.openWindow')}><AppWindow size={14} strokeWidth={1.75} /><span className="rec-btn-label"> {t('rec.openWindow')}</span></button>}
      </div>
    </div>
  );
}
